import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation, OnDestroy, AfterViewInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, forkJoin, interval, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from 'src/app/auth';
import {
  AnalysisSavedData,
  CheckIbanSRRequest,
  CheckIbanSRResponse,
  CodigoPostal,
  GenericResponse,
  IDraftDataInfoAnalisis,
  IDraftDataInfoContrato,
  IDraftDataInfoInstalacion,
  IDraftDataInfoRazonSocial,
  InstallationSavedData,
  PaymentSavedData,
  RazonSocial,
  UserInfo
} from 'src/app/interfaces';
import { CancelContractService } from 'src/app/services/cancel-contract.service';
import { NewContractPymesService } from 'src/app/services/new-contract-pymes.service';
import { UrlService } from 'src/app/services/url.service';
import { UtilService } from 'src/app/services/util.service';
import { ModalCancelContractComponent } from 'src/app/shared/modal-cancel-contract/modal-cancel-contract.component';
import { ModalErrorBankAccountComponent } from 'src/app/shared/modal-error-bank-account/modal-error-bank-account.component';
import { ToastrService } from 'src/app/shared/ui/toastr/toastr.service';
import { ConfirmDialogComponent } from './confirm-dialog/confirm-dialog.component';
import { DraftWarningComponent } from './draft-warning/draft-warning.component';
import { ExistingContractDialogComponent } from './existing-contract-dialog/existing-contract-dialog.component';
import { ResumeDialogComponent } from './resume-dialog/resume-dialog.component';
import { CheckContractAsyncResponse, CreateContractAsyncResponse } from './new-contract-pymes.model';
import { EnterMargins } from '../renovate-contract-pymes/models/renovate-contract-pymes.model';

@Component({
  selector: 'app-new-contract-pymes',
  templateUrl: './new-contract-pymes.component.html',
  styleUrls: ['./new-contract-pymes.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class NewContractPymesComponent implements OnInit, OnDestroy, AfterViewInit {
  private destroyed$: Subject<boolean> = new Subject<boolean>();
  public formValid = [false, false, false, false, false];
  public validForm = new BehaviorSubject<boolean>(false);
  public activeNext = new BehaviorSubject<boolean>(false);
  public savedDataBusiness: RazonSocial;
  public savedDataInstallation: InstallationSavedData;
  public savedDataAnalysis: AnalysisSavedData;
  public savedDataPayment: PaymentSavedData;
  public userData: UserInfo = null;
  public activeNextCRM = false;
  public hasContactoCRM: boolean;
  public contactoCRM: string;
  public draftEnabled = new BehaviorSubject<boolean>(false);
  public isDraft = false;
  public isPendingSign = false;
  public IDDraft: number = null;
  public codigoContrato: string = null;
  public codigoRazonSocial: string = null;
  public nombreRazonSocial: string = null;
  private disabledAddSepa = true;
  public tabIndex = new BehaviorSubject<number>(null);
  private installationFromCups: boolean;
  public isLoading = false;
  public contractType: number = null;
  public showTypeOptions = false;
  public isUserSpanish = true;
  public hasSepaFile = true;
  public hasContractFile = true;
  private allowAsyncContract: boolean;
  public asyncContractCreated = false;
  private hasCrmCode: string;

  constructor(
    private auth: AuthService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private newContractService: NewContractPymesService,
    private cancelContractService: CancelContractService,
    private cdRef: ChangeDetectorRef,
    public url: UrlService,
    private toastrService: ToastrService,
    private utilService: UtilService,
    private authService: AuthService,
  ) { }

  ngOnInit(): void {
    this.isUserSpanish = this.auth.getUserData().country === 'ES';
    this.allowAsyncContract = this.authService.getUserData().allowContratacionAsyncrona;
    this.newContractService.resetSavedData();
    this.isDraft = this.activatedRoute.snapshot.url[0].path === 'draft';

    this.newContractService.setIndexContract(0);
    if (this.isDraft) {
      this.contractType = 0;
      this.activatedRoute.queryParams.subscribe(params => {
        this.IDDraft = params.idDraft;
        this.codigoContrato = params.codigoContrato;
        this.codigoRazonSocial = params.codigoRazonSocial;
        this.nombreRazonSocial = params.nombreRazonSocial;
        this.disabledAddSepa = params.disabledAddSepa;
      });

      if (this.IDDraft) {
        this.getDraft();
      } else if (this.codigoContrato && this.codigoRazonSocial && this.nombreRazonSocial) {
        this.asyncContractCreated = true;
        this.isPendingSign = true;
        this.tabIndex.next(4);
        this.newContractService.setIndexContract(this.tabIndex.value);
        this.newContractService.setSavedData({
          codigoContrato: this.codigoContrato,
          installation: {
            codigoRazonSocial: this.codigoRazonSocial
          },
          business: {
            nombre: this.nombreRazonSocial
          },
          disabledAddSepa: this.disabledAddSepa,
          needFiles: true
        });
      } else {
        this.router.navigateByUrl('/home-pymes');
      }
    } else {
      this.showTypeOptions = true;
    }

    this.newContractService.getSavedData().pipe(takeUntil(this.destroyed$)).subscribe(res => {
      if (res) {
        this.savedDataBusiness = res.business;
        this.savedDataInstallation = res.installation;
        this.savedDataAnalysis = res.analysis;
        this.savedDataPayment = res.payment;
        this.installationFromCups = res.installationFromCups;
      }
    });

    this.userData = this.auth.getRole().code !== '300000004' ? this.auth.userData.data : null;

    if (!this.userData || this.isDraft) {
      this.newContractService.setSavedData({ contactoCRM: this.auth.userData.data.contactoCRM });
      this.hasContactoCRM = true;
    }

    this.validForm.subscribe(data => {
      this.activeNext.next(data);
      this.cdRef.detectChanges();
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  ngAfterViewInit(): void {
    this.cdRef.detectChanges();
  }

  private getDraft(): void {
    this.isLoading = true;
    this.newContractService.getDraft(this.IDDraft).pipe(takeUntil(this.destroyed$)).subscribe(res => {
      if (res) {
        const infoAnalisis = {
          cups: res.data.infoAnalisis.cups,
          cif: res.data.infoAnalisis.cif,
          accessFee: res.data.infoAnalisis.tarifaSips,
          consumption: res.data.infoAnalisis.consumoEstimado,
          campaignName: res.data.infoAnalisis.nombreCampana,
          campaign: res.data.infoAnalisis.codigoCampana,
          levelName: res.data.infoAnalisis.nombreNivel,
          level: res.data.infoAnalisis.nombreNivel,
          zonaGeograficaCUPS: res.data.infoAnalisis.zonaGeograficaCUPS,
          calculateCommission: res.data.infoAnalisis.indicadorMargen,
          unitsTE: res.data.infoAnalisis.unitsTE,
          unitsTP: res.data.infoAnalisis.unitsTP,
          enterMargins: res.data.infoAnalisis.enterMargins,
          calculateMargins: res.data.infoAnalisis.calculateMargins,
          commission: res.data.infoAnalisis.commission,
          campanaBase: res.data.infoAnalisis.campanaBase,
          consumoEstimadoPeriodo: res.data.infoAnalisis.consumoEstimadoPeriodo,
          potenciaContratadaPeriodo: res.data.infoAnalisis.potenciaContratadaPeriodo,
          comisionCanalEnergia: res.data.infoAnalisis.comisionCanalEnergia,
          comisionCanalPotencia: res.data.infoAnalisis.comisionCanalPotencia,
          tarifaSipsReal: res.data.infoAnalisis.tarifaSipsReal,
          idTension: res.data.infoAnalisis.idTension,
          tension: res.data.infoAnalisis.tensionCUPS,
          tensionName: res.data.infoAnalisis.nombreTensionCUPS,
          idCycle: res.data.infoAnalisis.idCiclo,
          cycle: res.data.infoAnalisis.cicloCUPS,
          cycleName: res.data.infoAnalisis.nombreCicloCUPS,
          idPeriod: res.data.infoAnalisis.idPeriodo,
          period: res.data.infoAnalisis.periodoCUPS,
          periodName: res.data.infoAnalisis.nombrePeriodoCUPS,
          existeEnRepin: res.data.infoAnalisis.existeEnRepin,
          otraPotencia: res.data.infoAnalisis.otraPotencia
        };
        const subscriptionsCP = [];
        if (res.data.infoRazonSocial.codPostalDireccion) {
          subscriptionsCP.push(this.newContractService.getCodigoPostal(res.data.infoRazonSocial.codPostalDireccion));
        }
        if (res.data.infoInstalacion.codPostalDireccion) {
          subscriptionsCP.push(this.newContractService.getCodigoPostal(res.data.infoInstalacion.codPostalDireccion));
        }
        if (res.data.infoContrato.codPostalDireccion) {
          subscriptionsCP.push(this.newContractService.getCodigoPostal(res.data.infoContrato.codPostalDireccion));
        }
        forkJoin(subscriptionsCP).pipe(takeUntil(this.destroyed$)).subscribe(
          (resList: GenericResponse<CodigoPostal>[]) => {
            if (resList.length > 0) {
              res.data.infoRazonSocial['nombreProvinciaDireccion'] = resList[0].data.nombreProvincia;
              res.data.infoRazonSocial['codigoProvinciaDireccion'] = resList[0].data.codigoProvincia;
              if (resList.length > 1) {
                res.data.infoInstalacion['nombreProvinciaDireccion'] = resList[1].data.nombreProvincia;
                res.data.infoInstalacion['codigoProvinciaDireccion'] = resList[1].data.codigoProvincia;
                if (resList.length > 2) {
                  res.data.infoContrato['nombreProvinciaDireccion'] = resList[2].data.nombreProvincia;
                  res.data.infoContrato['codigoProvinciaDireccion'] = resList[2].data.codigoProvincia;
                }
              }
            }
            this.newContractService.setSavedData({
              contactoCRM: res.data.codigoUserCRM,
              codigoRazonSocial: res.data.infoInstalacion?.codigoRazonSocial,
              analysis: infoAnalisis,
              business: res.data.infoRazonSocial,
              installation: res.data.infoInstalacion,
              payment: res.data.infoContrato
            });
            this.hasContactoCRM = true;
            this.tabIndex.next(res.data.numFormulario - 1);
            this.newContractService.setIndexContract(this.tabIndex.value);
          }, error => {
            this.utilService.toastErrors(error);
          });
        this.isLoading = false;
        this.newContractService.setSavedData({
          aprobado: res.data.aprobado,
          contactoCRM: res.data.codigoUserCRM,
          codigoRazonSocial: res.data.infoInstalacion?.codigoRazonSocial,
          codigoInstalacion: res.data.infoContrato?.codidoInstalacion,
          analysis: infoAnalisis,
          business: res.data.infoRazonSocial,
          installation: res.data.infoInstalacion,
          payment: res.data.infoContrato
        });
        this.hasContactoCRM = true;
        this.tabIndex.next(res.data.numFormulario - 1);
        this.newContractService.setIndexContract(this.tabIndex.value);
      }
    });
  }

  public formValidChange(e): void {
    this.validForm.next(e.value);
    this.formValid[e.tab] = e.value;
  }

  public setContactoCRM(contactoCRM): void {
    this.contactoCRM = contactoCRM;
    this.activeNextCRM = contactoCRM;
  }

  public close = (): void => {
    this.router.navigate([this.url.previousUrl.value]);
  }

  public activateDraft(e: boolean): void {
    this.draftEnabled.next(e);
  }

  public setLoading(loader: boolean): void {
    this.isLoading = loader;
  }

  public goNext = (): void => {
    this.activeNext.next(false);
    switch (this.tabIndex.value) {
      case 0:
        this.tabIndex.next(this.tabIndex.value + 1);
        this.activateDraft(true);
        if (this.newContractService.getSavedData().value.businessFromCups) {
          this.formValidChange({ value: true, tab: 1 });
        }
        this.saveDraft(2);
        break;
      case 1:
        this.isLoading = true;
        this.activateDraft(true);
        this.newContractService.setAltaRazonSocial(this.savedDataBusiness).pipe(takeUntil(this.destroyed$)).subscribe(
          res => {
            if (res.success) {
              this.isLoading = false;
              this.newContractService.setSavedData({ codigoRazonSocial: res.data });
              this.tabIndex.next(this.tabIndex.value + 1);
              if (this.newContractService.getSavedData().value.installationFromCups) {
                const savedInstallation = this.newContractService.getSavedData().value.installation;
                savedInstallation.codigoRazonSocial = res.data;
                savedInstallation.contactoCRM = this.newContractService.getSavedData().value.contactoCRM;
                savedInstallation.idBorrador = this.newContractService.getSavedData().value.idBorrador;
                savedInstallation.codigoCampana = this.newContractService.getSavedData().value.analysis.campaign.toString();
                this.newContractService.setSavedData({ installation: savedInstallation });
                if (
                  savedInstallation.nombre &&
                  savedInstallation.cups &&
                  savedInstallation.nombreTipoViaDireccion &&
                  savedInstallation.nombreDireccion &&
                  savedInstallation.codPostalDireccion &&
                  savedInstallation.nombrePoblacionDireccion
                ) {
                  this.formValidChange({ value: true, tab: 2 });
                } else {
                  this.formValidChange({ value: false, tab: 2 });
                }
              }
            } else {
              this.isLoading = false;
              this.utilService.toastErrors(res);
              this.activeNext.next(true);
            }
            this.cdRef.detectChanges();
          }, error => {
            this.errorManagement(error);
          }
        );
        break;
      case 2:
        this.isLoading = true;
        this.activateDraft(true);
        this.newContractService.setAltaInstalacion(this.savedDataInstallation).pipe(takeUntil(this.destroyed$)).subscribe(
          res => {
            if (res.success) {
              this.isLoading = false;
              this.newContractService.setSavedData({ codigoInstalacion: res.data });
              this.tabIndex.next(this.tabIndex.value + 1);
            } else {
              this.isLoading = false;
              this.utilService.toastErrors(res);
              this.activeNext.next(true);
            }
            this.cdRef.detectChanges();
          },
          error => {
            this.errorManagement(error);
          }
        );
        break;
      case 3:
        this.checkIbanRS();
        break;
    }
  }

  private checkIbanRS(): void {
    this.isLoading = true;
    this.newContractService.postCheckIbanRS(this.getBodyCheckIbanRS()).pipe(takeUntil(this.destroyed$)).subscribe(
      (res: GenericResponse<CheckIbanSRResponse>) => {
        if (res.success) {
          this.isLoading = false;
          if (res.data.contains) {
            this.openModalBankError();
          } else {
            this.confirmationModal();
          }
        } else {
          this.handleErrorResponse(res);
        }
      },
      error => {
        this.handleErrorResponse(error);
      });
  }

  private getBodyCheckIbanRS(): CheckIbanSRRequest {
    return {
      codRazonSocial: this.newContractService.getSavedData().value.codigoRazonSocial,
      codigosCuentasBancarias: [
        {
          iban: this.newContractService.getSavedData().value.payment.iban,
          entidad: this.newContractService.getSavedData().value.payment.entidad,
          sucursal: this.newContractService.getSavedData().value.payment.sucursal,
          dc: this.newContractService.getSavedData().value.payment.dc,
          cuenta: this.newContractService.getSavedData().value.payment.cuenta
        }
      ]
    };
  }

  private openModalBankError(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      disableClose: true,
      autoFocus: false,
      width: '575px',
      data: {
        multipoint: false,
      }
    };
    const dialogRef = this.dialog.open(ModalErrorBankAccountComponent, dialogConfig.data);
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.confirmationModal();
      } else {
        this.notGoNext();
      }
    });
  }

  private confirmationModal(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      height: '250px',
      width: '575px',
      disableClose: true
    };
    const dialogRef = this.dialog.open(ConfirmDialogComponent, dialogConfig.data);
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'yes') {
        this.isLoading = true;
        this.cdRef.detectChanges();
        this.activateDraft(true);
        let enterMarginsAsync = this.getEnterMargins(this.savedDataAnalysis.enterMargins);
        this.savedDataPayment.marginTE = enterMarginsAsync['marginsTE'];
        this.savedDataPayment.marginTP = enterMarginsAsync['marginTP'];
        if (this.allowAsyncContract) {
          this.createContractAsync();
        } else {
          this.newContractService.setAltaContrato(this.savedDataPayment).pipe(takeUntil(this.destroyed$)).subscribe(
            res => {
              if (res.success) {
                this.createContractSuccess(res);
              } else {
                this.createContractFailed(res);
              }
              this.cdRef.detectChanges();
            },
            error => {
              this.errorManagement(error);
            }
          );
        }
      } else {
        this.notGoNext();
      }
    });
  }

  private createContractSuccess(res: GenericResponse<{
    creadoAnteriormente: boolean,
    codigoCRMContrato: string
  } | CheckContractAsyncResponse>): void {
    this.isLoading = false;
    const crmCode = (res.data as any)?.codigoCrmContrato ? (res.data as any)?.codigoCrmContrato : (res.data as any)?.codigoCRMContrato;
    this.newContractService.setSavedData({ codigoContrato: crmCode, needFiles: false });
    if ((res.data as any)?.creadoAnteriormente) {
      this.openConfirmation();
    } else {
      this.tabIndex.next(this.tabIndex.value + 1);
      this.cdRef.detectChanges();
    }
  }

  private createContractFailed(res: GenericResponse<
    {
      creadoAnteriormente: boolean,
      codigoCRMContrato: string
    }
    | CreateContractAsyncResponse
    | CheckContractAsyncResponse
  >): void {
    this.isLoading = false;
    this.utilService.toastErrors(res);
    this.activeNext.next(true);
  }

  private createContractAsync(): void {
    this.toastrService.success('NEW_CONTRACTS.ASYNC_CREATING_CONTRACT_MESSAGE');
    this.newContractService.createContractAsync(this.savedDataPayment).pipe(takeUntil(this.destroyed$)).subscribe(
      res => {
        if (res.success) {
          this.asyncContractCreated = false;
          const intervalSubscription = interval(10000).pipe(takeUntil(this.destroyed$)).subscribe(() => {
            if (!this.asyncContractCreated) {
              this.checkCreatedContractAsync(res.data?.identificadorTemporalContrato);
            } else {
              intervalSubscription.unsubscribe();
            }
          });
        } else {
          this.createContractFailed(res);
        }
        this.cdRef.detectChanges();
      },
      error => {
        this.errorManagement(error);
      }
    );
  }

  private checkCreatedContractAsync(identifier: string): void {
    this.newContractService.checkContractAsync(identifier).pipe(takeUntil(this.destroyed$)).subscribe(
      res => {
        if (res.success) {
          if (res.data?.error) {
            if (res.data?.codigoCrmContrato) {
              this.createContractSuccess(res);
            }
            this.checkCreatedContractAsyncError(res.data?.descripcionError);
          } else {
            this.hasContractFile = res.data?.ficheroContrato;
            this.hasSepaFile = res.data?.ficheroSepa;
            if (res.data?.codigoCrmContrato && !this.hasCrmCode) {
              this.createContractSuccess(res);
              this.hasCrmCode = res.data.codigoCrmContrato;
            } else if (!res.data?.codigoCrmContrato && !this.hasCrmCode) {
              this.toastrService.success('NEW_CONTRACTS.ASYNC_CREATING_CONTRACT_MESSAGE');
            }
            if (res.data?.codigoCrmContrato && res.data?.ficheroContrato) {
              this.asyncContractCreated = true;
            } else if (res.data?.codigoCrmContrato && !res.data?.ficheroContrato) {
              this.toastrService.success('NEW_CONTRACTS.ASYNC_CREATING_CONTRACT_FILES_MESSAGE');
            }
            this.cdRef.detectChanges();
          }
        } else {
          this.createContractFailed(res);
        }
      }, error => {
        this.errorManagement(error);
      }
    );
  }

  private checkCreatedContractAsyncError(errorMessage: string): void {
    this.isLoading = false;
    this.toastrService.error(errorMessage);
    this.asyncContractCreated = true;
    this.activeNext.next(true);
    this.cdRef.detectChanges();
  }

  private notGoNext(): void {
    this.activeNext.next(true);
    this.cdRef.detectChanges();
  }

  private errorManagement(error): void {
    this.isLoading = false;
    this.utilService.toastErrors(error);
    this.activeNext.next(true);
    this.cdRef.detectChanges();
  }

  public goToStepper = (): void => {
    if (this.contractType === 0) {
      this.tabIndex.next(0);
      this.showTypeOptions = false;
    } else if (this.contractType === 1) {
      this.showTypeOptions = false;
      this.router.navigate(
        ['/new-contract-pymes-multipoint'], { queryParams: { crmContact: this.contactoCRM || this.auth.userData.data.contactoCRM } }
      );
    } else {
      this.newContractService.setSavedData({ contactoCRM: this.contactoCRM });
      this.hasContactoCRM = true;
      this.activeNextCRM = false;
      this.showTypeOptions = true;
    }
  }

  public tabChange(e): void {
    this.tabIndex.next(e.index);
    this.validForm.next(this.formValid[e.index]);
    this.newContractService.setIndexContract(e.index);
    this.activateDraft(true);
  }

  public openResume(event): void {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      autoFocus: false,
      height: window.screen.height,
      width: '472px',
      position: {
        right: '0px'
      },
      data: event
    };

    this.dialog.open(ResumeDialogComponent, dialogConfig.data);
  }

  private openConfirmation(): void {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      autoFocus: false,
      height: '300px',
      width: '575px',
      disableClose: true
    };

    const dialogRef = this.dialog.open(ExistingContractDialogComponent, dialogConfig.data);
    dialogRef.afterClosed().subscribe(result => {
      if (result === 'exit') {
        this.router.navigate([this.url.previousUrl.value]);
      } else {
        this.tabIndex.next(this.tabIndex.value + 1);
        this.cdRef.detectChanges();
      }
    });
  }

  private openDraftWarn(): void {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      autoFocus: false,
      height: '580px',
      width: '575px'
    };

    this.dialog.open(DraftWarningComponent, dialogConfig.data);
  }

  public saveDraft(numFormulario: number): void {
    this.isLoading = true;
    const infoAnalisisDraft: IDraftDataInfoAnalisis = {
      cups: null,
      cif: null,
      zonaGeograficaCUPS: null,
      consumoEstimado: 0,
      tarifaSips: null,
      codigoCampana: null,
      nombreCampana: null,
      nombreNivel: null,
      indicadorMargen: null,
      unitsTE: null,
      unitsTP: null,
      enterMargins: null,
      calculateMargins: null,
      commission: null,
      campanaBase: null,
      consumoEstimadoPeriodo: null,
      potenciaContratadaPeriodo: null,
      comisionCanalEnergia: null,
      comisionCanalPotencia: null,
      tarifaSipsReal: null,
      idCiclo: null,
      cicloCUPS: null,
      nombreCicloCUPS: null,
      idTension: null,
      tensionCUPS: null,
      nombreTensionCUPS: null,
      idPeriodo: null,
      periodoCUPS: null,
      nombrePeriodoCUPS: null,
      existeEnRepin: null,
      otraPotencia: null
    };
    let infoRazonSocialDraft: IDraftDataInfoRazonSocial;
    let infoInstalacionDraft: IDraftDataInfoInstalacion;
    let infoContratoDraft: IDraftDataInfoContrato;
    const idBorrador = this.newContractService.getSavedData().value.idBorrador;
    const infoAnalisis = this.newContractService.getSavedData().value.analysis;
    infoAnalisisDraft.cups = (infoAnalisis && infoAnalisis.cups) || null;
    infoAnalisisDraft.cif = (infoAnalisis && infoAnalisis.cif) || null;
    infoAnalisisDraft.consumoEstimado = (infoAnalisis && (infoAnalisis.consumption || infoAnalisis.consumption === 0))
      ? infoAnalisis.consumption : null;
    infoAnalisisDraft.tarifaSips = (infoAnalisis && infoAnalisis.accessFee) || null;
    infoAnalisisDraft.codigoCampana = (infoAnalisis && infoAnalisis.campaign) || null;
    infoAnalisisDraft.nombreCampana = (infoAnalisis && infoAnalisis.campaignName) || null;
    infoAnalisisDraft.nombreNivel = (infoAnalisis && infoAnalisis.levelName) || null;
    infoAnalisisDraft.zonaGeograficaCUPS = (infoAnalisis && infoAnalisis.zonaGeograficaCUPS) || null;
    infoAnalisisDraft.indicadorMargen = (infoAnalisis && infoAnalisis.calculateCommission) || null;
    infoAnalisisDraft.unitsTE = (infoAnalisis && infoAnalisis.unitsTE) || null;
    infoAnalisisDraft.unitsTP = (infoAnalisis && infoAnalisis.unitsTP) || null;
    infoAnalisisDraft.enterMargins = (infoAnalisis && this.getEnterMargins(infoAnalisis.enterMargins)) || null;
    infoAnalisisDraft.calculateMargins = (infoAnalisis && infoAnalisis.calculateMargins) || null;
    infoAnalisisDraft.commission = (infoAnalisis && infoAnalisis.commission) || null;
    infoAnalisisDraft.campanaBase = (infoAnalisis && infoAnalisis.campanaBase) || null;
    infoAnalisisDraft.consumoEstimadoPeriodo = (infoAnalisis && infoAnalisis.consumoEstimadoPeriodo) || null;
    infoAnalisisDraft.potenciaContratadaPeriodo = (infoAnalisis && infoAnalisis.potenciaContratadaPeriodo) || null;
    infoAnalisisDraft.comisionCanalEnergia = (infoAnalisis && infoAnalisis.comisionCanalEnergia) || null;
    infoAnalisisDraft.comisionCanalPotencia = (infoAnalisis && infoAnalisis.comisionCanalPotencia) || null;
    infoAnalisisDraft.tarifaSipsReal = (infoAnalisis && infoAnalisis.tarifaSipsReal) || null;
    infoAnalisisDraft.cicloCUPS = (infoAnalisis && infoAnalisis.cycle) || null;
    infoAnalisisDraft.nombreCicloCUPS = (infoAnalisis && infoAnalisis.cycleName) || null;
    infoAnalisisDraft.nombreTensionCUPS = (infoAnalisis && infoAnalisis.tensionName) || null;
    infoAnalisisDraft.tensionCUPS = (infoAnalisis && infoAnalisis.tension) || null;
    infoAnalisisDraft.nombrePeriodoCUPS = (infoAnalisis && infoAnalisis.periodName) || null;
    infoAnalisisDraft.periodoCUPS = (infoAnalisis && infoAnalisis.period) || null;
    infoAnalisisDraft.existeEnRepin = infoAnalisis?.existeEnRepin;
    infoAnalisisDraft.otraPotencia = infoAnalisis?.otraPotencia;
    if (this.isUserSpanish) {
      delete infoAnalisisDraft.existeEnRepin;
      delete infoAnalisisDraft.otraPotencia;
    }

    infoRazonSocialDraft = this.newContractService.getSavedData().value.business
      ? this.newContractService.getSavedData().value.business
      : {
        nombre: null,
        cif: null,
        cnae: null,
        nombreContactoComercial: null,
        apellidosContactoComercial: null,
        dniContactoComercial: null,
        telefonoContactoComercial: null,
        mailContactoComercial: null,
        nombreTipoViaDireccion: null,
        nombreDireccion: null,
        numeroDireccion: null,
        portalDireccion: null,
        escaleraDireccion: null,
        pisoDireccion: null,
        puertaDireccion: null,
        codPostalDireccion: null,
        codigoPoblacionDireccion: null,
        codigoProvinciaDireccion: null
      };
    infoInstalacionDraft = this.newContractService.getSavedData().value.installation
      ? this.newContractService.getSavedData().value.installation
      : {
        codigoRazonSocial: null,
        codigoCampana: null,
        nombre: null,
        cups: null,
        nombreTipoViaDireccion: null,
        nombreDireccion: null,
        numeroDireccion: null,
        portalDireccion: null,
        escaleraDireccion: null,
        pisoDireccion: null,
        puertaDireccion: null,
        codPostalDireccion: null,
        codigoPoblacionDireccion: null
      };
    infoContratoDraft = this.newContractService.getSavedData().value.payment
      ? this.newContractService.getSavedData().value.payment
      : {
        codidoInstalacion: null,
        codigoCampana: null,
        nombreNivel: null,
        nombreFirmante: null,
        apellidosFirmante: null,
        dniFirmante: null,
        movilFirmante: null,
        mailFirmante: null,
        nombreTipoViaDireccion: null,
        nombreDireccion: null,
        numeroDireccion: null,
        portalDireccion: null,
        escaleraDireccion: null,
        pisoDireccion: null,
        puertaDireccion: null,
        codPostalDireccion: null,
        codigoPoblacionDireccion: null,
        iban: null,
        entidad: null,
        sucursal: null,
        dc: null,
        cuenta: null,
        fechaVigenciaFutura: null
      };
    const draftData = {
      idBorrador,
      numFormulario,
      infoAnalisis: infoAnalisisDraft,
      infoRazonSocial: infoRazonSocialDraft,
      infoInstalacion: infoInstalacionDraft,
      infoContrato: infoContratoDraft
    };
    this.newContractService.setDraft(idBorrador, draftData).pipe(takeUntil(this.destroyed$)).subscribe(
      (res: any) => {
        if (res.success) {
          this.toastrService.error('NEW_CONTRACTS.DRAFT_SAVED');
        } else {
          this.utilService.toastErrors(res);
        }
        this.isLoading = false;
        this.cdRef.detectChanges();
      },
      error => {
        this.isLoading = false;
        this.utilService.toastErrors(error);
        this.cdRef.detectChanges();
      }
    );
  }

  public setContractType(value): void {
    this.contractType = Number(value);
    this.activeNextCRM = true;
  }

  public cancelContract(isDraft: boolean): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      height: '250px',
      width: '575px',
      disableClose: true
    };
    const dialogRef = this.dialog.open(ModalCancelContractComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.isLoading = true;
        if (isDraft && this.tabIndex.value !== 4) {
          this.cancelContractService.cancelDraft(
            this.newContractService.getSavedData().value.idBorrador
          ).pipe(takeUntil(this.destroyed$)).subscribe(
            (response: GenericResponse) => {
              const userNotice = 'NEW_CONTRACTS.SUCCESS_CANCEL_DRAFT';
              this.handleResponse(response, userNotice);
            },
            error => {
              this.handleErrorResponse(error);
            }
          );
        } else {
          this.cancelContractService.cancelContract(
            this.newContractService.getSavedData().value.codigoContrato
          ).pipe(takeUntil(this.destroyed$)).subscribe(
            (response: GenericResponse) => {
              const userNotice = 'NEW_CONTRACTS.SUCCESS_CANCEL';
              this.handleResponse(response, userNotice);
            },
            error => {
              this.handleErrorResponse(error);
            }
          );
        }
      }
    });
  }

  private handleErrorResponse(error: any): void {
    this.utilService.toastErrors(error);
    this.isLoading = false;
  }

  private handleResponse(response: GenericResponse, userNotice: string): void {
    if (response.success) {
      this.toastrService.success(userNotice);
      this.router.navigate([this.url.previousUrl.value]);
    } else {
      this.utilService.toastErrors(response);
    }
    this.isLoading = false;
  }

  public getEnterMargins(enterMargins: EnterMargins): EnterMargins {
    return {
      marginsTE: {
        p1: enterMargins?.marginsTE?.p1?.toString().replace(',', '.') || null,
        p2: enterMargins?.marginsTE?.p2?.toString().replace(',', '.') || null,
        p3: enterMargins?.marginsTE?.p3?.toString().replace(',', '.') || null,
        p4: enterMargins?.marginsTE?.p4?.toString().replace(',', '.') || null,
        p5: enterMargins?.marginsTE?.p5?.toString().replace(',', '.') || null,
        p6: enterMargins?.marginsTE?.p6?.toString().replace(',', '.') || null,
      },
      marginTP: enterMargins?.marginTP?.toString().replace(',', '.') || null
    } 
  }
}
